import { state } from '../core/state.js';
import { dayKey } from '../core/text.js';
import { streakDays } from '../progress/streak.js';
import { renderCurrency, saveProgress } from '../progress/xp.js';
import { confetti } from '../ui/confetti.js';
import { SFX, pushPop, toast } from '../ui/toast.js';
import { ADV_GROUPS, ADV_ITEMS, ADV_PETS, ITEM_COST, ITEM_MAX, PET_MAX, SLOT_CAP, gPetBonusVal, groupMaxWear } from './data.js';
import { itemBonusText, refreshArenaStats, renderGearPanel } from './equip.js';
import { RPG } from './ui.js';
import { SKILL_TREE } from './world.js';

/* Item-Zustand: {level 1–5, copies = Fortschritt zur nächsten Stufe} */
export function gItem(id) { const it = G().items[id]; return (it && typeof it === 'object') ? it : null; }
export function gItemBonus(id) { const it = gItem(id); return it ? (it.level + (it.ench || 0)) * ADV_ITEMS[id].per : 0; }
export const ENCH_MAX = 3;
export const ENCH_COST = { 0: 4, 1: 8, 2: 15 };   // Diamanten je Verzauberungsstufe
export function gEnchantItem(id) {
  const g = G(); const it = gItem(id); if (!it) return;
  const ench = it.ench || 0;
  if (ench >= ENCH_MAX) { toast('Bereits maximal verzaubert (+3)'); return; }
  const cost = ENCH_COST[ench];
  if (state.progress.gems < cost) { toast(`Du brauchst ${cost} 💎 zum Verzaubern`); return; }
  state.progress.gems -= cost; it.ench = ench + 1;
  try { SFX.win(); } catch (e) {} confetti();
  saveProgress(); renderCurrency(); renderGearPanel(); refreshArenaStats();
  pushPop({ img: RPG(ADV_ITEMS[id].img), eyebrow: '✨ Verzaubert!', name: `${ADV_ITEMS[id].name} · Verzauberung +${it.ench}`, reward: itemBonusText(id, it.level) });
}
/* Kopien hinzufügen: legt neu an (Stufe 1) oder füllt/levelt bestehendes Item. */
export function gAddItemCopies(id, copies) {
  const g = G(); let overflow = 0;
  if (!gItem(id)) { g.items[id] = { level: 1, copies: 0 }; copies -= 1; }   // erste Kopie = Item auf Stufe 1
  const it = g.items[id];
  while (copies > 0 && it.level < ITEM_MAX) {
    const need = ITEM_COST[it.level] - it.copies;
    const take = Math.min(copies, need);
    it.copies += take; copies -= take;
    if (it.copies >= ITEM_COST[it.level]) { it.copies -= ITEM_COST[it.level]; it.level++; }
  }
  if (it.level >= ITEM_MAX) { it.copies = 0; overflow = copies; }            // maxed → Rest als Münzen
  return { level: it.level, overflow };
}

export function defaultGame() {
  return {
    on: true, intro: false,
    charXp: 0, charLvl: 1,
    section: 1, maxSection: 1,
    obstacle: null,
    up: { knowledge: 0, focus: 0, collection: 0, insight: 0, combo: 0, fortune: 0 },
    items: {}, equip: { head: [], face: [], trinket: [] },
    chests: 0, focusEnergy: 0, chestProgress: 0, examsBeaten: 0,
    fokusSpecial: 'power', critStreak: 0, carryDmg: 0,
    skillNodes: {},
    pets: {}, activePet: null, setFullEver: 0,
    groupsBeaten: 0, bossSquadBeaten: 0, elitesBeaten: 0,
    lastFreeChest: '', duel: { week: '', startMax: 1 },
    quest: { day: '', tasks: [], claimed: false },
    weekly: { week: '', task: { type: 'cards', goal: 150, prog: 0 }, claimed: false },
    lastTick: null, lastStudy: null, sessionDefeats: 0,
  };
}
export function gameMigrate(g) {
  const d = defaultGame();
  if (!g) return d;
  const items = {};
  Object.keys(g.items || {}).forEach((id) => {
    const v = g.items[id];
    items[id] = (v && typeof v === 'object') ? { level: v.level || 1, copies: v.copies || 0, ench: v.ench || 0 } : { level: v || 1, copies: 0, ench: 0 };
  });
  // Ausrüstung auf 2-pro-Platz (Arrays) migrieren; alte Einzel-Slots übernehmen.
  const oldEq = g.equip || {};
  const equip = { head: [], face: [], trinket: [] };
  ['head', 'face', 'trinket'].forEach((s) => {
    const v = oldEq[s];
    if (Array.isArray(v)) equip[s] = v.filter((id) => ADV_ITEMS[id]).slice(0, SLOT_CAP);
    else if (v && ADV_ITEMS[v]) equip[s] = [v];
  });
  // verwaiste Item-Ids (aus alten Katalogen) verwerfen
  Object.keys(items).forEach((id) => { if (!ADV_ITEMS[id]) delete items[id]; });
  // Haustiere migrieren: altes `true` → {level:1}; verwaiste Ids verwerfen
  const pets = {}; Object.keys(g.pets || {}).forEach((id) => {
    if (!ADV_PETS[id]) return;
    const v = g.pets[id];
    pets[id] = (v && typeof v === 'object') ? { level: Math.min(PET_MAX, v.level || 1) } : { level: 1 };
  });
  const activePet = (g.activePet && ADV_PETS[g.activePet] && pets[g.activePet]) ? g.activePet : null;
  return { ...d, ...g, up: { ...d.up, ...(g.up || {}) }, items, equip, pets, activePet, skillNodes: { ...(g.skillNodes || {}) }, quest: { ...d.quest, ...(g.quest || {}) }, weekly: { ...d.weekly, ...(g.weekly || {}) }, duel: { ...d.duel, ...(g.duel || {}) } };
}
/* Wochen-Fortschritt fürs Freundesduell: Abschnitte, die DIESE Woche geschafft wurden. */
export function gWeekAdvance() {
  const g = G(); const wk = weekKey();
  if (!g.duel || g.duel.week !== wk) g.duel = { week: wk, startMax: g.maxSection };
  return Math.max(0, g.maxSection - g.duel.startMax);
}
/* Tagesziele: ≥10 Vorlagen, täglich 2 zufällige (aber pro Tag stabil). */
export const QUEST_POOL = [
  { type: 'defeat', goal: 3,  label: (g) => `⚔ Besiege ${g} Lernhindernisse` },
  { type: 'defeat', goal: 6,  label: (g) => `⚔ Besiege ${g} Lernhindernisse` },
  { type: 'cards',  goal: 15, label: (g) => `🗂 Lerne ${g} Karten` },
  { type: 'cards',  goal: 30, label: (g) => `🗂 Lerne ${g} Karten` },
  { type: 'good',   goal: 12, label: (g) => `😊 ${g}× „Gut" oder besser antworten` },
  { type: 'crit',   goal: 5,  label: (g) => `✨ Lande ${g} Geistesblitze` },
  { type: 'fokus',  goal: 2,  label: (g) => `⚡ Setze ${g} Fokus-Spezialangriffe ein` },
  { type: 'chest',  goal: 2,  label: (g) => `🎁 Öffne ${g} Schatztruhen` },
  { type: 'combo',  goal: 8,  label: (g) => `🔥 Erreiche eine Kombo von x${g}` },
  { type: 'exam',   goal: 1,  label: (g) => `📜 Bestehe ${g} Wissensprüfung` },
  { type: 'level',  goal: 1,  label: (g) => `🌟 Steige ${g} Lernstufe(n) auf` },
  { type: 'easy',   goal: 8,  label: (g) => `💜 ${g}× „Sehr leicht" antworten` },
];
export const WEEKLY_POOL = [
  { type: 'defeat', goal: 25,  label: (g) => `⚔ Besiege ${g} Lernhindernisse` },
  { type: 'cards',  goal: 150, label: (g) => `🗂 Lerne ${g} Karten` },
  { type: 'exam',   goal: 3,   label: (g) => `📜 Bestehe ${g} Wissensprüfungen` },
  { type: 'good',   goal: 100, label: (g) => `😊 ${g}× „Gut" oder besser antworten` },
];
export function hashStr(s) { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; }
export function seededPick(pool, key, n) {
  const idx = pool.map((_, i) => i); let seed = hashStr(key) || 1;
  const rng = () => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 4294967296; };
  for (let i = idx.length - 1; i > 0; i--) { const j = Math.floor(rng() * (i + 1)); [idx[i], idx[j]] = [idx[j], idx[i]]; }
  return idx.slice(0, n).map((i) => pool[i]);
}
export function weekKey() {
  const d = new Date(); const jan = new Date(d.getFullYear(), 0, 1);
  const w = Math.ceil((((d - jan) / 86400000) + jan.getDay() + 1) / 7);
  return d.getFullYear() + '-W' + w;
}
export function gQuest() {
  const g = G(); const today = dayKey();
  if (!g.quest || g.quest.day !== today || !Array.isArray(g.quest.tasks)) {
    g.quest = { day: today, tasks: seededPick(QUEST_POOL, today, 2).map((p) => ({ type: p.type, goal: p.goal, prog: 0 })), claimed: false };
  }
  return g.quest;
}
export function gWeekly() {
  const g = G(); const wk = weekKey();
  if (!g.weekly || g.weekly.week !== wk) {
    const p = seededPick(WEEKLY_POOL, wk, 1)[0];
    g.weekly = { week: wk, task: { type: p.type, goal: p.goal, prog: 0 }, claimed: false };
  }
  return g.weekly;
}
export const questLabel = (pool, type, goal) => { const t = pool.find((p) => p.type === type && p.goal === goal) || pool.find((p) => p.type === type); return t ? t.label(goal) : type; };
export function gQuestDone() { return gQuest().tasks.every((t) => t.prog >= t.goal); }
export function gWeeklyDone() { const w = gWeekly(); return w.task.prog >= w.task.goal; }
/* Fortschritt an Tages- UND Wochenziel melden. isMax = „erreiche X" statt „sammle X". */
export function gQuestProgress(type, amount = 1, isMax = false) {
  const bump = (t) => { if (t.type !== type) return; if (isMax) t.prog = Math.max(t.prog, amount); else t.prog = Math.min(t.goal, t.prog + amount); };
  gQuest().tasks.forEach(bump); bump(gWeekly().task);
}
export const G = () => (state.progress.game || (state.progress.game = defaultGame()));

export function gStats() {
  const g = G();
  const mastered = state.cards.filter((c) => (c.interval || 0) >= 21).length;
  const total = state.cards.length;
  const streak = streakDays();
  // Nur AUSGERÜSTETE Items geben Boni (2 pro Platz).
  const equipped = gEquipped();
  let gearK = 0, gearCrit = 0, coinBonus = 0, luck = 0;
  const groupCount = {};
  equipped.forEach((id) => {
    if (!gItem(id)) return;
    const it = ADV_ITEMS[id], stat = it.stat, bon = gItemBonus(id);
    if (stat === 'knowledge') gearK += bon; else if (stat === 'crit') gearCrit += bon;
    else if (stat === 'coin') coinBonus += bon; else if (stat === 'luck') luck += bon;
    if (it.group) groupCount[it.group] = (groupCount[it.group] || 0) + 1;
  });
  // Set-Boni: 2 Teile = b2, 3+ = b3, komplettes Set (Max tragbar) = bFull
  const sets = [];
  Object.keys(groupCount).forEach((gid) => {
    const n = groupCount[gid]; if (n < 2) return;
    const grp = ADV_GROUPS[gid]; const full = groupMaxWear(gid);
    const isFull = n >= full; const tier = isFull ? 4 : n >= 3 ? 3 : 2;
    const bon = isFull ? grp.bFull : n >= 3 ? grp.b3 : grp.b2;
    if (grp.stat === 'knowledge') gearK += bon; else if (grp.stat === 'crit') gearCrit += bon;
    else if (grp.stat === 'coin') coinBonus += bon; else if (grp.stat === 'luck') luck += bon;
    if (isFull && !g.setFullEver) g.setFullEver = 1;   // Meilenstein: komplettes Set einmal getragen
    sets.push({ id: gid, n, tier, bon, full });
  });
  // Aktives Haustier: Passiv-Bonus (skaliert mit Haustier-Stufe)
  const pet = g.activePet && g.pets && g.pets[g.activePet] ? ADV_PETS[g.activePet] : null;
  if (pet) {
    const pb = gPetBonusVal(g.activePet);
    if (pet.stat === 'knowledge') gearK += pb; else if (pet.stat === 'crit') gearCrit += pb;
    else if (pet.stat === 'coin') coinBonus += pb; else if (pet.stat === 'luck') luck += pb;
  }
  // Talentbaum: freigeschaltete Passiv-Knoten
  if (typeof SKILL_TREE !== 'undefined') SKILL_TREE.forEach((n) => {
    if (!n.stat || !(g.skillNodes && g.skillNodes[n.id])) return;
    if (n.stat === 'knowledge') gearK += n.val; else if (n.stat === 'crit') gearCrit += n.val;
    else if (n.stat === 'coin') coinBonus += n.val; else if (n.stat === 'luck') luck += n.val;
  });
  const knowledge = (5 + 2 * g.charLvl + 0.5 * mastered + 2 * g.up.knowledge) * (1 + gearK);
  const crit = Math.min(50, 5 + 0.5 * streak + g.up.insight + gearCrit);
  const collect = 1 + 0.15 * g.up.collection;
  const coinsPerMin = knowledge * 0.4 * collect * (1 + 0.03 * streak) * (1 + coinBonus);
  return { mastered, total, streak, knowledge, crit, collect, coinsPerMin, coinBonus, luck, sets, gearK };
}
/* Alle aktuell getragenen Item-Ids (aus den 2-er-Plätzen). */
export function gEquipped() {
  const eq = G().equip || {};
  return ['head', 'face', 'trinket'].flatMap((s) => (Array.isArray(eq[s]) ? eq[s] : (eq[s] ? [eq[s]] : []))).filter(Boolean);
}
export function gIsEquipped(id) { return gEquipped().includes(id); }
export const gXpToNext = (L) => Math.round(40 * Math.pow(1.25, L - 1));
export function gLevelUp() {
  const g = G(); let ups = 0;
  // Jede Lernstufe gibt: +Angriffskraft (über die Formel) UND eine Schatztruhe.
  while (g.charXp >= gXpToNext(g.charLvl)) { g.charXp -= gXpToNext(g.charLvl); g.charLvl++; g.chests += 1; ups++; }
  if (ups) gQuestProgress('level', ups);
  return ups;
}
